import { EnvironmentProviders, Injector, Provider } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { CURRENT_LOCALE } from '@app/ui/stores';
import { DekError } from '@app/domain/entities';
import type {
  IDatabaseService,
  IRepositoryFactory,
  IHttpClient,
  ISettingsRepository,
  IGoogleAuthAdapter,
  ICryptoAdapter,
  IBackupRepository,
  IBackupQuotaAdapter,
  IGoogleDriveAdapter,
  IMetadataKeyRepository,
} from '@app/domain/ports';
import { BackupEncryptionAdapter, BackupQuotaAdapter, BackupRemoteAdapter } from '@app/infrastructure/backup';
import { OnnxCategorizationAdapter } from '@app/infrastructure/ml';
import { DekService } from '@app/domain/services';
import { AppStore } from '../states';
import { STORAGE_KEYS, SETTINGS } from '@app/domain/ports';
import {
  DatabaseService,
  EncryptedBankCacheAdapter,
  EncryptedDatabasePersistenceAdapter,
  MccRepository,
  StorageLifecycleManager,
} from '@app/infrastructure/storage';
import { NbuCurrencyRateProvider } from '@app/infrastructure/nbu';
import { AngularHttpClientAdapter } from '@app/infrastructure/http';
import { CryptoAdapter } from '@app/infrastructure/crypto';
import { WebAuthnAdapter } from '@app/infrastructure/webauthn';
import { AndroidBiometricAdapter } from '@app/infrastructure/android-keystore';
import { GoogleAuthAdapter, GoogleDriveAdapter } from '@app/infrastructure/google-drive';
import { EncryptedSettingsRepositoryDecorator } from '../decorators/encrypted-settings-repository.decorator';
import { environment } from '@app/environment';
import { RuntimeConfigService } from '../services/runtime-config.service';
import { DEFAULT_BACKUP_PATH } from '../services/backup-path.constants';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { FEATURE_FLAGS } from '../services/feature-flags.constants';
import {
  BANK_REQUEST_CACHE_TTL_MS,
  ACTIVE_SQL_MODE,
  CAPACITOR_SQL_MODE,
  DATABASE_SERVICE,
  HTTP_CLIENT,
  REPOSITORY_FACTORY,
  ACCOUNT_REPOSITORY,
  ACCOUNT_TRANSFER_REPOSITORY,
  BACKUP_REPOSITORY,
  BANK_REQUEST_CACHE_REPOSITORY,
  BANK_STATEMENT_CACHE_PORT,
  CATEGORY_REPOSITORY,
  CHANGE_TRACKER,
  CREDITOR_REPOSITORY,
  CREDITOR_PAYMENT_REPOSITORY,
  DATABASE_PERSISTENCE_ADAPTER,
  DEBTOR_REPOSITORY,
  DEBTOR_PAYMENT_REPOSITORY,
  CURRENCY_REPOSITORY,
  CURRENCY_EXCHANGE_REPOSITORY,
  EXPENSE_REPOSITORY,
  PLANNED_EXPENSE_REPOSITORY,
  EXTERNAL_TRANSACTION_REPOSITORY,
  INCOME_REPOSITORY,
  PLANNED_INCOME_REPOSITORY,
  MCC_REPOSITORY,
  METADATA_KEY_REPOSITORY,
  RATE_REPOSITORY,
  REPORT_REPOSITORY,
  REPORTING_REPOSITORY,
  SETTINGS_REPOSITORY,
  TAG_REPOSITORY,
  CRYPTO_ADAPTER,
  BIOMETRIC_AUTH,
  GOOGLE_AUTH_ADAPTER,
  GOOGLE_DRIVE_ADAPTER,
  DATABASE_ADAPTER,
  BACKUP_ENCRYPTION_ADAPTER,
  BACKUP_REMOTE_ADAPTER,
  BACKUP_QUOTA_ADAPTER,
  TRANSACTION_MANAGER,
  TRANSACTION_CATEGORIZATION,
  WEB_SQL_MODE,
  RUNTIME_KIND,
  type RuntimeKind,
  type SqlMode,
} from '../tokens/infrastructure.tokens';
import { CURRENCY_RATE_PROVIDER } from '@app/ui/stores';
import { NativeReportExportFileService } from '../../features/reports/report-viewer/export/native-report-export-file.service';
import { REPORT_EXPORT_FILE_SERVICE } from '../../features/reports/report-viewer/export/report-export-file.service';
import { WebReportExportFileService } from '../../features/reports/report-viewer/export/web-report-export-file.service';

export function resolveBackupPath(settings: ISettingsRepository): string {
  const configuredPath = settings.get<string>(SETTINGS.BACKUP_PATH, DEFAULT_BACKUP_PATH);
  return typeof configuredPath === 'string' && configuredPath.trim() ? configuredPath : DEFAULT_BACKUP_PATH;
}

/**
 * Provides Infrastructure Layer adapters and implementations.
 *
 * Infrastructure layer is the outermost layer in Clean Architecture,
 * providing concrete implementations of ports defined in the domain layer.
 *
 * Includes:
 * - Database Service: SQLite WASM adapter
 * - HTTP Client: Angular HttpClient adapter
 * - Repository Factory: Creates all repository implementations
 * - 13 Repository implementations: Account, Currency, Expense, etc.
 * - External API providers: NBU Currency Rate Provider
 * - Change Tracker: Database change monitoring
 *
 * All providers follow the port-adapter pattern, where domain defines
 * the interface (port) and infrastructure provides the implementation (adapter).
 */
export function provideInfrastructure(): (EnvironmentProviders | Provider)[] {
  return [
    // Configuration values
    {
      provide: BANK_REQUEST_CACHE_TTL_MS,
      useValue: environment.requestCacheTtlMs,
    },
    {
      provide: WEB_SQL_MODE,
      useValue: environment.webSqlMode,
    },
    {
      provide: CAPACITOR_SQL_MODE,
      useValue: environment.capacitorSqlMode,
    },
    {
      provide: ACTIVE_SQL_MODE,
      useFactory: (runtimeKind: RuntimeKind, webSqlMode: SqlMode, capacitorSqlMode: SqlMode) =>
        runtimeKind === 'capacitor' ? capacitorSqlMode : webSqlMode,
      deps: [RUNTIME_KIND, WEB_SQL_MODE, CAPACITOR_SQL_MODE],
    },

    // Crypto Adapter - port-adapter pattern (cryptographic operations)
    {
      provide: CRYPTO_ADAPTER,
      useClass: CryptoAdapter,
    },

    // Biometric Authentication — WebAuthn PRF on PWA, Android Keystore on Capacitor/Android.
    // Both adapters implement IBiometricAuthPort so the existing use cases
    // (ActivateWebAuthnUseCase, UnlockDekWithWebAuthnUseCase) work unchanged on both runtimes.
    {
      provide: BIOMETRIC_AUTH,
      useFactory: (runtimeKind: RuntimeKind, translate: TranslateService) =>
        runtimeKind === 'capacitor'
          ? new AndroidBiometricAdapter((key: string) => translate.instant(key))
          : new WebAuthnAdapter(),
      deps: [RUNTIME_KIND, TranslateService],
    },

    {
      provide: REPORT_EXPORT_FILE_SERVICE,
      useFactory: (runtimeKind: RuntimeKind) =>
        runtimeKind === 'capacitor' ? new NativeReportExportFileService() : new WebReportExportFileService(),
      deps: [RUNTIME_KIND],
    },

    // Google Auth Adapter - port-adapter pattern (OAuth 2.0 + PKCE)
    {
      provide: GOOGLE_AUTH_ADAPTER,
      useFactory: (
        settings: ISettingsRepository,
        crypto: CryptoAdapter,
        runtimeConfig: RuntimeConfigService,
        runtimeKind: RuntimeKind,
      ) =>
        new GoogleAuthAdapter(
          settings,
          () => {
            const config = {
              clientId: runtimeConfig.googleClientId,
              functionsUrl: runtimeConfig.functionsUrl,
              authorizationLanguage: settings.get<string>(SETTINGS.INTERFACE_LANGUAGE, 'en') as string,
              // Capacitor uses /oauth-callback (Firebase serves oauth-callback.html
              // which fires the intent:// URL to hand control back to the native app).
              // PWA uses /oauth-callback-web (Angular SPA handles it directly via
              // oauthCallbackGuard) so an intent:// from oauth-callback.html cannot
              // hijack a PWA OAuth flow on an Android device that also has the APK
              // installed.
              redirectUri:
                runtimeKind === 'capacitor'
                  ? runtimeConfig.redirectBaseUrl + '/oauth-callback'
                  : runtimeConfig.redirectBaseUrl + '/oauth-callback-web',
              scopes: 'https://www.googleapis.com/auth/drive.file',
            };

            if (runtimeKind === 'capacitor') {
              // Open the OAuth URL in a Chrome Custom Tab instead of the WebView.
              // Google blocks sign-in from embedded WebViews; Chrome Custom Tabs
              // are allowed.  The HTTPS redirect URI is served by Firebase Hosting
              // as oauth-callback.html, which redirects to the custom scheme
              // declared by the installed app so Capacitor's appUrlOpen event can
              // deliver the code + state back to the correct debug or release build.
              return {
                ...config,
                prepareCustomState: async (customState?: Record<string, unknown>) => {
                  try {
                    const { App } = await import('@capacitor/app');
                    const { id: appId } = await App.getInfo();

                    return {
                      ...customState,
                      nativeTarget: {
                        packageName: appId,
                        scheme: appId,
                      },
                    };
                  } catch (error) {
                    console.warn('[GoogleAuth] Failed to resolve native app identity for OAuth callback', error);
                    return customState;
                  }
                },
                openUrl: (url: string) => import('@capacitor/browser').then(({ Browser }) => Browser.open({ url })),
              };
            }
            return config;
          },
          crypto,
        ),
      deps: [SETTINGS_REPOSITORY, CRYPTO_ADAPTER, RuntimeConfigService, RUNTIME_KIND],
    },

    // Google Drive Adapter - port-adapter pattern (Drive API)
    {
      provide: GOOGLE_DRIVE_ADAPTER,
      useFactory: (auth: IGoogleAuthAdapter) => new GoogleDriveAdapter(auth),
      deps: [GOOGLE_AUTH_ADAPTER],
    },

    // Database Service - port-adapter pattern (main entry point)
    {
      provide: DATABASE_SERVICE,
      useFactory: (bankRequestCacheTtlMs: number, sqliteMode: SqlMode) =>
        new DatabaseService({ bankRequestCacheTtlMs, sqliteMode }),
      deps: [BANK_REQUEST_CACHE_TTL_MS, ACTIVE_SQL_MODE],
    },

    // Storage lifecycle manager - orchestrates baseline storage + optional OPFS pre-init.
    {
      provide: StorageLifecycleManager,
      useFactory: (
        sqliteMode: SqlMode,
        databaseService: IDatabaseService,
        metadataRepository: IMetadataKeyRepository,
        runtimeKind: RuntimeKind,
      ) => new StorageLifecycleManager(sqliteMode, databaseService, metadataRepository, runtimeKind === 'capacitor'),
      deps: [ACTIVE_SQL_MODE, DATABASE_SERVICE, METADATA_KEY_REPOSITORY, RUNTIME_KIND],
    },

    // HTTP Client - port-adapter pattern (Angular → framework-agnostic)
    {
      provide: HTTP_CLIENT,
      useFactory: (httpClient: HttpClient) => new AngularHttpClientAdapter(httpClient),
      deps: [HttpClient],
    },

    // Currency Rate Provider - depends on IHttpClient port (not Angular HttpClient)
    {
      provide: CURRENCY_RATE_PROVIDER,
      useFactory: (httpClient: IHttpClient) => new NbuCurrencyRateProvider(httpClient),
      deps: [HTTP_CLIENT],
    },

    // MCC Repository - reference data repository (doesn't use DatabaseService)
    {
      provide: MCC_REPOSITORY,
      useFactory: (httpClient: IHttpClient) => new MccRepository(httpClient),
      deps: [HTTP_CLIENT],
    },

    // Repository Factory - obtained from DatabaseService (SQLite WASM + IndexedDB + localStorage)
    {
      provide: REPOSITORY_FACTORY,
      useFactory: (db: IDatabaseService) => db.getRepositoryFactory(),
      deps: [DATABASE_SERVICE],
    },

    {
      provide: CHANGE_TRACKER,
      useFactory: (factory: IRepositoryFactory) => factory.getChangeTracker(),
      deps: [REPOSITORY_FACTORY],
    },

    // Repository ports - created by factory (app doesn't know about SQLite!)
    {
      provide: ACCOUNT_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createAccountRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: CURRENCY_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createCurrencyRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: ACCOUNT_TRANSFER_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createAccountTransferRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: CATEGORY_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createCategoryRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: CREDITOR_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createCreditorRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: CREDITOR_PAYMENT_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createCreditorPaymentRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: DEBTOR_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createDebtorRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: DEBTOR_PAYMENT_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createDebtorPaymentRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: EXPENSE_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createExpenseRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: PLANNED_EXPENSE_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createPlannedExpenseRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: PLANNED_INCOME_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createPlannedIncomeRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: TRANSACTION_MANAGER,
      useFactory: (factory: IRepositoryFactory) => factory.createTransactionManager(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: INCOME_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createIncomeRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: RATE_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createCurrencyRateRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: CURRENCY_EXCHANGE_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createCurrencyExchangeRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: EXTERNAL_TRANSACTION_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createExternalTransactionRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: METADATA_KEY_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createMetadataKeyRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: BACKUP_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createBackupRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: REPORT_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createReportRepository(),
      deps: [REPOSITORY_FACTORY],
    },
    {
      provide: BANK_REQUEST_CACHE_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createBankRequestCacheRepository(),
      deps: [REPOSITORY_FACTORY],
    },

    {
      provide: REPORTING_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createReportingRepository(),
      deps: [REPOSITORY_FACTORY],
    },

    {
      provide: TAG_REPOSITORY,
      useFactory: (factory: IRepositoryFactory) => factory.createTagRepository(),
      deps: [REPOSITORY_FACTORY],
    },

    // Settings Repository - created by REPOSITORY_FACTORY, then wrapped with encryption decorator
    {
      provide: SETTINGS_REPOSITORY,
      useFactory: (factory: IRepositoryFactory, injector: Injector) => {
        const baseRepository = factory.createSettingsRepository();
        return new EncryptedSettingsRepositoryDecorator(baseRepository, injector);
      },
      deps: [REPOSITORY_FACTORY, Injector],
    },

    // Backup adapter ports — pure-TS lib classes constructed via factories
    // Each factory supplies app-layer deps (DekService, AppStore, environment)
    // via constructor callbacks so the lib classes stay framework-agnostic.
    { provide: DATABASE_ADAPTER, useExisting: DATABASE_SERVICE },

    {
      provide: BACKUP_ENCRYPTION_ADAPTER,
      useFactory: (crypto: ICryptoAdapter, dek: DekService, metadataRepo: IMetadataKeyRepository) =>
        new BackupEncryptionAdapter(crypto, async () => {
          let dekHex: string | null = null;
          await dek.getDekContainer().execute(async (key) => {
            if (!key) throw new DekError('unlock', 'DEK not unlocked');
            const raw = await crypto.exportRawKey(key);
            dekHex = Array.from(raw)
              .map((b) => b.toString(16).padStart(2, '0'))
              .join('');
          });
          if (!dekHex) throw new DekError('export-dek', 'Failed to export DEK');

          const meta = await metadataRepo.get<{ iv: ArrayBuffer; data: ArrayBuffer }>(
            STORAGE_KEYS.PASSWORD_ENCRYPTED_BY_DEK,
          );
          if (!meta) throw new DekError('decrypt-master-password', 'Master password not found.');
          let password: string | null = null;
          await dek.getDekContainer().execute(async (key) => {
            if (!key) throw new DekError('unlock', 'DEK not unlocked');
            password = await crypto.decryptString(new Uint8Array(meta.data), new Uint8Array(meta.iv), key);
          });
          if (!password) throw new DekError('decrypt-master-password', 'Failed to decrypt master password');
          return [dekHex, password];
        }),
      deps: [CRYPTO_ADAPTER, DekService, METADATA_KEY_REPOSITORY],
    },

    {
      provide: BACKUP_QUOTA_ADAPTER,
      useFactory: (backupRepo: IBackupRepository, appStore: InstanceType<typeof AppStore>) =>
        new BackupQuotaAdapter(backupRepo, () => appStore.backupQuotaMB() * 1024 * 1024),
      deps: [BACKUP_REPOSITORY, AppStore],
    },

    {
      provide: BACKUP_REMOTE_ADAPTER,
      useFactory: (
        storage: IGoogleDriveAdapter,
        backupRepo: IBackupRepository,
        quotaAdapter: IBackupQuotaAdapter,
        settings: ISettingsRepository,
      ) => new BackupRemoteAdapter(storage, backupRepo, quotaAdapter, () => resolveBackupPath(settings)),
      deps: [GOOGLE_DRIVE_ADAPTER, BACKUP_REPOSITORY, BACKUP_QUOTA_ADAPTER, SETTINGS_REPOSITORY],
    },

    // ML transaction categorization — ONNX-based adapter (lazy-loaded, offline-capable)
    {
      provide: TRANSACTION_CATEGORIZATION,
      useFactory: (featureFlags: FeatureFlagsService, settings: ISettingsRepository) =>
        new OnnxCategorizationAdapter(
          () => featureFlags.isEnabled(FEATURE_FLAGS.ML_CATEGORIZATION_ENABLED),
          () => settings.get<string>(SETTINGS.ML_CURRENT_JSON_URL, '/ml/current.json'),
        ),
      deps: [FeatureFlagsService, SETTINGS_REPOSITORY],
    },

    // Bank Statement Cache — encrypted adapter wrapping IBankRequestCacheRepository
    {
      provide: BANK_STATEMENT_CACHE_PORT,
      useFactory: (
        bankCacheRepo: ReturnType<IRepositoryFactory['createBankRequestCacheRepository']>,
        crypto: ICryptoAdapter,
        dek: DekService,
        appStore: InstanceType<typeof AppStore>,
      ) =>
        new EncryptedBankCacheAdapter(
          bankCacheRepo,
          crypto,
          () => dek.getDekContainer(),
          () => appStore.encryptData(),
        ),
      deps: [BANK_REQUEST_CACHE_REPOSITORY, CRYPTO_ADAPTER, DekService, AppStore],
    },

    // Database Persistence — encrypted load/save of the SQLite snapshot to IndexedDB
    {
      provide: DATABASE_PERSISTENCE_ADAPTER,
      useFactory: (
        crypto: ICryptoAdapter,
        dek: DekService,
        metadataRepo: IMetadataKeyRepository,
        appStore: InstanceType<typeof AppStore>,
      ) =>
        new EncryptedDatabasePersistenceAdapter(
          crypto,
          () => dek.getDekContainer(),
          metadataRepo,
          () => appStore.isUnlocked(),
        ),
      deps: [CRYPTO_ADAPTER, DekService, METADATA_KEY_REPOSITORY, AppStore],
    },

    // UI store tokens — wired to app-layer signals
    {
      provide: CURRENT_LOCALE,
      useFactory: (appStore: InstanceType<typeof AppStore>) => () => appStore.currentLocale(),
      deps: [AppStore],
    },
  ];
}
